import { create } from 'zustand';

export type Operation = {
  id: string;
  label: string;
  startedAt: number;
  progress?: number;
  totalSteps?: number;
  currentStep?: number;
  phase?: string | null;
  currentLabel?: string | null;
  etaMs?: number | null;
  elapsedMs?: number | null;
  filesPerSecond?: number | null;
  errorsCount?: number | null;
  /** Folder path key for scan timing baselines (local library). */
  baselineKey?: string | null;
  /** Wall-clock ms when the current `phase` began (for per-step elapsed/countdown). */
  phaseStartedAt?: number | null;
};

type OperationPatch = Partial<Pick<
  Operation,
  'label' | 'progress' | 'totalSteps' | 'currentStep' |
  'phase' | 'currentLabel' | 'etaMs' | 'elapsedMs' | 'filesPerSecond' | 'errorsCount' |
  'baselineKey' | 'phaseStartedAt'
>>;

type OperationProgressState = {
  operations: Record<string, Operation>;
  beginOperation: (label: string, initial?: OperationPatch) => string;
  updateOperation: (id: string, patch: OperationPatch) => void;
  updateOperationProgress: (id: string, fields: {
    current?: number | null;
    total?: number | null;
    phase?: string | null;
    currentLabel?: string | null;
    etaMs?: number | null;
    filesPerSecond?: number | null;
    errorsCount?: number | null;
  }) => void;
  endOperation: (id: string) => void;
  getActive: () => Operation[];
};

function clampProgress(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.max(0, Math.min(1, value));
}

function makeOpId(): string {
  return `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const useOperationProgressStore = create<OperationProgressState>((set, get) => ({
  operations: {},

  beginOperation: (label, initial) => {
    const id = makeOpId();
    const op: Operation = {
      id,
      label: label || 'Working...',
      startedAt: Date.now(),
      progress: clampProgress(initial?.progress),
      totalSteps: initial?.totalSteps,
      currentStep: initial?.currentStep,
      phase: initial?.phase,
      baselineKey: initial?.baselineKey,
      phaseStartedAt: initial?.phaseStartedAt ?? Date.now(),
    };
    set((state) => ({
      operations: {
        ...state.operations,
        [id]: op,
      },
    }));
    return id;
  },

  updateOperation: (id, patch) => {
    if (!id) return;
    set((state) => {
      const existing = state.operations[id];
      if (!existing) return state;
      return {
        operations: {
          ...state.operations,
          [id]: {
            ...existing,
            ...patch,
            progress: clampProgress(patch.progress ?? existing.progress),
          },
        },
      };
    });
  },

  updateOperationProgress: (id, fields) => {
    if (!id) return;
    set((state) => {
      const existing = state.operations[id];
      if (!existing) return state;
      const { current, total, phase, currentLabel, etaMs, filesPerSecond, errorsCount } = fields;
      const progress = (typeof current === 'number' && typeof total === 'number' && total > 0)
        ? clampProgress(current / total)
        : existing.progress;
      return {
        operations: {
          ...state.operations,
          [id]: {
            ...existing,
            progress,
            currentStep: typeof current === 'number' ? current : existing.currentStep,
            totalSteps: typeof total === 'number' ? total : existing.totalSteps,
            phase: phase !== undefined ? phase : existing.phase,
            currentLabel: currentLabel !== undefined ? currentLabel : existing.currentLabel,
            etaMs: etaMs !== undefined ? etaMs : existing.etaMs,
            filesPerSecond: filesPerSecond !== undefined ? filesPerSecond : existing.filesPerSecond,
            errorsCount: errorsCount !== undefined ? errorsCount : existing.errorsCount,
          },
        },
      };
    });
  },

  endOperation: (id) => {
    if (!id) return;
    set((state) => {
      if (!state.operations[id]) return state;
      const next = { ...state.operations };
      delete next[id];
      return { operations: next };
    });
  },

  getActive: () =>
    Object.values(get().operations).sort((a, b) => a.startedAt - b.startedAt),
}));
